<script setup lang="ts">
import { ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { ArrowDown, ArrowUp, Landmark, Pencil, Plus } from '@lucide/vue';
import type { TipologiaClienteVista } from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { useAvvisi } from '@/composables/useAvvisi';
import { useInvalidation } from '@/composables/useInvalidation';
import { settingsKeys } from '../query-keys';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import StatoCaricamento from '@/components/ui/StatoCaricamento.vue';
import StatoVuoto from '@/components/ui/StatoVuoto.vue';
import FormTipologiaDialog from '../components/FormTipologiaDialog.vue';
import NavImpostazioni from '../components/NavImpostazioni.vue';
import {
  disattivaTipologiaCliente,
  listaTipologieCliente,
  riattivaTipologiaCliente,
  riordinaTipologieCliente,
} from '../api';

definePage({
  meta: { titolo: 'Forme giuridiche', privilegi: ['can_manage_tipologie_cliente'] },
});

const avvisi = useAvvisi();
// Il nome di una forma giuridica compare anche su schede ed elenco clienti.
const invalidate = useInvalidation('impostazioni', 'clienti');

const { data: tipologie, isLoading } = useQuery({
  queryKey: settingsKeys.clientLegalForms(),
  queryFn: listaTipologieCliente,
});

const dialogAperto = ref(false);
const inModifica = ref<TipologiaClienteVista | null>(null);
const azioneInCorso = ref<number | null>(null);

function apriCreazione(): void {
  inModifica.value = null;
  dialogAperto.value = true;
}
function apriModifica(t: TipologiaClienteVista): void {
  inModifica.value = t;
  dialogAperto.value = true;
}

async function toggleAttivo(t: TipologiaClienteVista): Promise<void> {
  azioneInCorso.value = t.id;
  try {
    if (t.attivo) {
      await disattivaTipologiaCliente(t.id);
      avvisi.successo('Disattivata');
    } else {
      await riattivaTipologiaCliente(t.id);
      avvisi.successo('Riattivata');
    }
    await invalidate();
  } catch (errore) {
    const messaggio =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Errore', messaggio);
  } finally {
    azioneInCorso.value = null;
  }
}

async function sposta(indice: number, delta: -1 | 1): Promise<void> {
  const righe = [...(tipologie.value ?? [])];
  const nuovoIndice = indice + delta;
  if (nuovoIndice < 0 || nuovoIndice >= righe.length) return;
  [righe[indice], righe[nuovoIndice]] = [righe[nuovoIndice], righe[indice]];

  try {
    await riordinaTipologieCliente(righe.map((t) => t.id));
    await invalidate();
  } catch (errore) {
    const messaggio = errore instanceof EvolfinApiError ? errore.message : 'Riordino non riuscito';
    avvisi.errore('Errore', messaggio);
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header class="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Impostazioni</p>
        <h1 class="mt-1 font-display text-[26px] leading-tight font-semibold">Forme giuridiche</h1>
      </div>
      <PulsanteAzione @click="apriCreazione">
        Nuova forma giuridica
        <template #icona><Plus :size="16" :stroke-width="2" /></template>
      </PulsanteAzione>
    </header>

    <NavImpostazioni />

    <StatoCaricamento v-if="isLoading" />

    <StatoVuoto v-else-if="!tipologie?.length" :icona="Landmark" titolo="Nessuna forma giuridica" />

    <ul v-else class="card flex flex-col divide-y divide-line-soft overflow-hidden">
      <li
        v-for="(t, indice) in tipologie"
        :key="t.id"
        class="flex items-center gap-3 px-4 py-3"
        :class="{ 'opacity-55': !t.attivo }"
      >
        <div class="flex shrink-0 flex-col">
          <button
            type="button"
            class="rounded-sm p-0.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink disabled:pointer-events-none disabled:opacity-30"
            title="Sposta su"
            :disabled="indice === 0"
            @click="sposta(indice, -1)"
          >
            <ArrowUp :size="14" :stroke-width="2" />
          </button>
          <button
            type="button"
            class="rounded-sm p-0.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink disabled:pointer-events-none disabled:opacity-30"
            title="Sposta giù"
            :disabled="indice === tipologie.length - 1"
            @click="sposta(indice, 1)"
          >
            <ArrowDown :size="14" :stroke-width="2" />
          </button>
        </div>

        <div class="min-w-0 flex-1">
          <p class="truncate text-[13.5px] font-medium">{{ t.nome }}</p>
          <p v-if="!t.attivo" class="mt-0.5 text-[12px] text-ink-muted">Disattivata</p>
        </div>

        <div class="flex shrink-0 items-center gap-1">
          <button
            type="button"
            class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
            title="Modifica"
            @click="apriModifica(t)"
          >
            <Pencil :size="16" :stroke-width="1.8" />
          </button>
          <PulsanteAzione
            variante="silenzioso"
            :caricamento="azioneInCorso === t.id"
            @click="toggleAttivo(t)"
          >
            {{ t.attivo ? 'Disattiva' : 'Riattiva' }}
          </PulsanteAzione>
        </div>
      </li>
    </ul>

    <FormTipologiaDialog v-model="dialogAperto" :tipologia="inModifica" @salvato="invalidate()" />
  </div>
</template>
